import type { CSSProperties } from 'react';
import { assets, type AssetKey } from '@/assets/manifest';

export interface AssetImageProps {
  /** Clave del manifiesto de FORGE-BRAVO. Tipada: una clave que no existe no compila. */
  asset: AssetKey;
  /** Valor del atributo `sizes`. Sin él el navegador asume 100vw y baja de más. */
  sizes: string;
  /**
   * `alt` propio de la página. Si se omite se usa el del manifiesto, que ya viene
   * en español y descriptivo. Para una imagen puramente decorativa se pasa `''`,
   * y entonces se marca además `aria-hidden`.
   */
  alt?: string;
  /** `eager` solo para lo que está sobre el pliegue (el hero). */
  loading?: 'eager' | 'lazy';
  className?: string;
  style?: CSSProperties;
}

/**
 * `<picture>` con AVIF -> WebP -> fallback, tomando TODO del manifiesto tipado
 * (ADR-004 §4): ninguna página escribe una ruta de imagen a mano.
 *
 * `width`/`height` van siempre puestos: son los intrínsecos de la derivada mayor
 * y reservan el hueco, así que no hay salto de layout al cargar.
 */
export function AssetImage({ asset, sizes, alt, loading = 'lazy', className, style }: AssetImageProps) {
  const item = assets[asset];
  const texto = alt ?? item.alt;
  const decorativa = texto === '';

  return (
    <picture>
      {item.sources.map((source) => (
        <source key={source.format} type={source.type} srcSet={source.srcSet} sizes={sizes} />
      ))}
      <img
        src={item.src}
        alt={texto}
        aria-hidden={decorativa || undefined}
        width={item.width}
        height={item.height}
        loading={loading}
        decoding="async"
        className={className}
        style={style}
      />
    </picture>
  );
}
